import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import {
  Heart, Cloud, Sun, Zap, Moon, AlertCircle, Play, Volume2,
  ChevronLeft, ChevronRight, Mic, Square, Bot, User, Send, RefreshCw
} from 'lucide-react';

// ==================== GROQ KEY ====================
const GROQ_KEY = process.env.REACT_APP_GROQ_KEY || '';

// ==================== CONTEXT ====================
const EntriesContext = createContext();

export const useEntries = () => {
  const context = useContext(EntriesContext);
  if (!context) throw new Error('useEntries must be used within EntriesProvider');
  return context;
};

const EntriesProvider = ({ children }) => {
  const [entries, setEntries] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('mindful_entries') || '[]');
    } catch { return []; }
  });

  const addEntry = (entry) => {
    const newEntry = { id: Date.now(), timestamp: new Date(), ...entry };
    setEntries(prev => {
      const updated = [newEntry, ...prev];
      try {
        localStorage.setItem('mindful_entries', JSON.stringify(updated));
      } catch {}
      return updated;
    });
    return newEntry;
  };

  return (
    <EntriesContext.Provider value={{ entries, addEntry }}>
      {children}
    </EntriesContext.Provider>
  );
};

// ==================== HELPERS ====================
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const getStreak = (entries) => {
  if (!entries.length) return 0;
  let streak = 0;
  for (let i = 0; i < 60; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    if (entries.some(e => new Date(e.timestamp).toDateString() === d.toDateString())) streak++;
    else break;
  }
  return streak;
};

// ==================== GLOBAL STYLES ====================
const GlobalStyles = () => (
  <style dangerouslySetInnerHTML={{__html: `
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { background: #F5F3F0; color: #1A1A1A; font-family: 'IBM Plex Mono', monospace; }
    @keyframes breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
    @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes worryBurner { 0% { opacity:1; transform:translateY(0) scale(1); filter:blur(0px); } 100% { opacity:0; transform:translateY(-100px) scale(0.8); filter:blur(8px); } }
    @keyframes worryRelease { 0% { opacity:1; transform:translateY(0) scale(1); filter:blur(0px); } 100% { opacity:0; transform:translateY(-80px) scale(0.8); filter:blur(6px); } }
    .worry-burn { animation: worryBurner 1.5s ease-out forwards; }
    @keyframes dash { to { stroke-dashoffset: 0; } }
    .fade-in { animation: fadeInUp 0.6s ease-out forwards; }
    .mono { font-family: 'IBM Plex Mono', monospace; }
    .dotted-border { border: 2px dotted #4169E1; }
    .blueprint-line { stroke: #4169E1; stroke-width: 2; fill: none; }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    .recording-pulse { animation: pulse 1.5s ease-in-out infinite; }
    @keyframes bubbleRise { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-600px) scale(1.2); opacity: 0.8; } }
    .bubble { animation: bubbleRise linear forwards; }
    @keyframes thinkingPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
  `}} />
);

// ==================== BUBBLE POP ====================
const BubblePop = () => {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const bubbleIdCounter = useRef(0);
  const gameInterval = useRef(null);

  const colors = [
    { color: '#FF6B9D', glow: 'rgba(255,107,157,0.6)' },
    { color: '#C44569', glow: 'rgba(196,69,105,0.6)' },
    { color: '#FFC048', glow: 'rgba(255,192,72,0.6)' },
    { color: '#51C4D3', glow: 'rgba(81,196,211,0.6)' },
    { color: '#7F5AF0', glow: 'rgba(127,90,240,0.6)' },
    { color: '#2CB67D', glow: 'rgba(44,182,125,0.6)' },
  ];

  const playPopSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  const startGame = () => {
    setGameActive(true);
    setScore(0);
    setBubbles([]);
    bubbleIdCounter.current = 0;
    gameInterval.current = setInterval(() => {
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      const newBubble = {
        id: bubbleIdCounter.current++,
        x: Math.random() * 85,
        size: Math.random() * 40 + 50,
        speed: Math.random() * 3 + 4,
        color: randomColor.color,
        glow: randomColor.glow
      };
      setBubbles(prev => [...prev, newBubble]);
    }, 800);
  };

  const stopGame = useCallback(() => {
    setGameActive(false);
    if (gameInterval.current) clearInterval(gameInterval.current);
    setScore(prev => {
      setHighScore(h => prev > h ? prev : h);
      return prev;
    });
  }, []);

  const popBubble = (id) => {
    playPopSound();
    setBubbles(prev => prev.filter(b => b.id !== id));
    setScore(prev => prev + 10);
  };

  useEffect(() => {
    return () => { if (gameInterval.current) clearInterval(gameInterval.current); };
  }, []);

  return (
    <div style={{border:'2px dotted #4169E1', padding:'24px', background:'#1a1a2e'}}>
      <div style={{marginBottom:'20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <p className="mono" style={{fontSize:'18px', color:'#FFF', fontWeight:'600'}}>SCORE: {score}</p>
          <p className="mono" style={{fontSize:'11px', color:'#AAA'}}>HIGH SCORE: {highScore}</p>
        </div>
        {!gameActive ? (
          <button className="mono" onClick={startGame} style={{padding:'12px 32px', fontSize:'14px', color:'#1a1a2e', background:'#2CB67D', border:'none', cursor:'pointer', letterSpacing:'0.1em', fontWeight:'600', borderRadius:'8px'}}>START</button>
        ) : (
          <button className="mono" onClick={stopGame} style={{padding:'12px 32px', fontSize:'14px', color:'#FFF', background:'#C44569', border:'none', cursor:'pointer', letterSpacing:'0.1em', fontWeight:'600', borderRadius:'8px'}}>STOP</button>
        )}
      </div>
      <div style={{position:'relative', width:'100%', height:'500px', background:'linear-gradient(to bottom, #0f3460, #16213e)', overflow:'hidden', borderRadius:'12px', boxShadow:'inset 0 0 50px rgba(0,0,0,0.5)'}}>
        {bubbles.map(bubble => (
          <div key={bubble.id} className="bubble" onClick={() => popBubble(bubble.id)}
            style={{position:'absolute', left:`${bubble.x}%`, bottom:'0', width:`${bubble.size}px`, height:`${bubble.size}px`, borderRadius:'50%', background:`radial-gradient(circle at 30% 30%, ${bubble.color}FF, ${bubble.color}CC)`, border:`3px solid ${bubble.color}`, cursor:'pointer', boxShadow:`0 0 30px ${bubble.glow}, inset -10px -10px 20px rgba(0,0,0,0.3)`, animationDuration:`${bubble.speed}s`, transition:'transform 0.1s'}}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          />
        ))}
        {!gameActive && bubbles.length === 0 && (
          <div style={{position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center'}}>
            <p className="mono" style={{fontSize:'16px', color:'#AAA'}}>Click START to begin popping bubbles!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== SANDFALL ====================
const Sandfall = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(5);
  const [selectedElement, setSelectedElement] = useState('sand');
  const gridRef = useRef(null);
  const animationRef = useRef(null);

  const elements = {
    sand: { color: '#E6C79C', behavior: 'powder' },
    water: { color: '#4A90E2', behavior: 'liquid' },
    stone: { color: '#666666', behavior: 'solid' },
    fire: { color: '#FF6B6B', behavior: 'fire' },
    plant: { color: '#52B788', behavior: 'plant' },
    wood: { color: '#8B4513', behavior: 'solid' },
    oil: { color: '#2C1810', behavior: 'liquid' },
    ice: { color: '#B0E0E6', behavior: 'solid' },
    lava: { color: '#FF4500', behavior: 'liquid' },
    gas: { color: '#D3D3D3', behavior: 'gas' }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = 700;
    const height = canvas.height = 500;
    const cellSize = 3;
    const cols = Math.floor(width / cellSize);
    const rows = Math.floor(height / cellSize);
    gridRef.current = Array(rows).fill(null).map(() => Array(cols).fill(null));
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    const simulate = () => {
      const grid = gridRef.current;
      const newGrid = grid.map(row => [...row]);
      for (let y = rows - 2; y >= 0; y--) {
        for (let x = 0; x < cols; x++) {
          if (!grid[y][x]) continue;
          const element = grid[y][x];
          if (element.behavior === 'powder') {
            if (!grid[y+1][x]) { newGrid[y][x]=null; newGrid[y+1][x]=element; }
            else if (x>0 && !grid[y+1][x-1] && Math.random()>0.5) { newGrid[y][x]=null; newGrid[y+1][x-1]=element; }
            else if (x<cols-1 && !grid[y+1][x+1] && Math.random()>0.5) { newGrid[y][x]=null; newGrid[y+1][x+1]=element; }
          } else if (element.behavior === 'liquid') {
            if (!grid[y+1][x]) { newGrid[y][x]=null; newGrid[y+1][x]=element; }
            else if (x>0 && !grid[y][x-1] && Math.random()>0.6) { newGrid[y][x]=null; newGrid[y][x-1]=element; }
            else if (x<cols-1 && !grid[y][x+1] && Math.random()>0.6) { newGrid[y][x]=null; newGrid[y][x+1]=element; }
            else if (x>1 && !grid[y+1][x-1]) { newGrid[y][x]=null; newGrid[y+1][x-1]=element; }
            else if (x<cols-2 && !grid[y+1][x+1]) { newGrid[y][x]=null; newGrid[y+1][x+1]=element; }
          } else if (element.behavior === 'gas') {
            if (y>0 && !grid[y-1][x] && Math.random()>0.3) { newGrid[y][x]=null; newGrid[y-1][x]=element; }
            else if (x>0 && !grid[y][x-1] && Math.random()>0.5) { newGrid[y][x]=null; newGrid[y][x-1]=element; }
            else if (x<cols-1 && !grid[y][x+1] && Math.random()>0.5) { newGrid[y][x]=null; newGrid[y][x+1]=element; }
          } else if (element.behavior === 'fire') {
            if (Math.random()<0.05) newGrid[y][x]=null;
          }
        }
      }
      gridRef.current = newGrid;
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      for (let y=0; y<rows; y++) {
        for (let x=0; x<cols; x++) {
          if (newGrid[y][x]) { ctx.fillStyle=newGrid[y][x].color; ctx.fillRect(x*cellSize, y*cellSize, cellSize, cellSize); }
        }
      }
      animationRef.current = requestAnimationFrame(simulate);
    };
    simulate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, []);

  const addParticles = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((clientX - rect.left) / 3);
    const y = Math.floor((clientY - rect.top) / 3);
    const grid = gridRef.current;
    const element = elements[selectedElement];
    for (let dy=-brushSize; dy<=brushSize; dy++) {
      for (let dx=-brushSize; dx<=brushSize; dx++) {
        if (dx*dx+dy*dy<=brushSize*brushSize) {
          const px=x+dx, py=y+dy;
          if (py>=0 && py<grid.length && px>=0 && px<grid[0].length) {
            grid[py][px]={ color:element.color, behavior:element.behavior };
          }
        }
      }
    }
  };

  const handleMouseDown = (e) => { setIsDrawing(true); addParticles(e.clientX, e.clientY); };
  const handleMouseMove = (e) => { if (isDrawing) addParticles(e.clientX, e.clientY); };
  const handleMouseUp = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle='#000000';
    ctx.fillRect(0,0,canvas.width,canvas.height);
    gridRef.current=Array(Math.floor(500/3)).fill(null).map(()=>Array(Math.floor(700/3)).fill(null));
  };

  return (
    <div style={{border:'2px dotted #4169E1', padding:'24px', background:'#1a1a1a'}}>
      <div style={{marginBottom:'16px', display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center'}}>
        {Object.keys(elements).map(key => (
          <button key={key} className="mono" onClick={() => setSelectedElement(key)} style={{padding:'10px 16px', fontSize:'11px', background:selectedElement===key?elements[key].color:'#333', color:selectedElement===key?'#000':'#FFF', border:selectedElement===key?`3px solid ${elements[key].color}`:'2px solid #555', cursor:'pointer', letterSpacing:'0.1em', fontWeight:'600', textTransform:'uppercase', borderRadius:'4px'}}>{key}</button>
        ))}
        <div style={{flex:1}} />
        <input type="range" min="1" max="15" value={brushSize} onChange={(e)=>setBrushSize(parseInt(e.target.value))} style={{width:'120px'}} />
        <span className="mono" style={{color:'#AAA', fontSize:'11px'}}>SIZE: {brushSize}</span>
        <button className="mono" onClick={clearCanvas} style={{padding:'10px 20px', fontSize:'11px', background:'#C44569', color:'#FFF', border:'none', cursor:'pointer', letterSpacing:'0.1em', fontWeight:'600', borderRadius:'4px'}}>CLEAR</button>
      </div>
      <canvas ref={canvasRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{border:'2px solid #333', cursor:'crosshair', display:'block', background:'#000', borderRadius:'8px'}} />
    </div>
  );
};

// ==================== COLOR CRUSH ====================
const ColorCrush = () => {
  const [grid, setGrid] = useState([]);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [moves, setMoves] = useState(30);
  const [selectedCell, setSelectedCell] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [nextColor, setNextColor] = useState(null);

  const colors = ['#FF6B6B','#4ECDC4','#FFD93D','#A8E6CF','#FF8B94','#C7CEEA'];
  const gridSize = 8;

  useEffect(() => {
    initializeGrid();
    const saved = localStorage.getItem('colorcrush_best');
    if (saved) setBestScore(parseInt(saved));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playCrushSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime+0.15);
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime+0.15);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime+0.15);
    } catch (error) { console.error('Audio error:', error); }
  };

  const getRandomColor = () => colors[Math.floor(Math.random()*colors.length)];

  const findMatches = (currentGrid) => {
    const matches = new Set();
    for (let row=0; row<gridSize; row++) {
      for (let col=0; col<gridSize-2; col++) {
        const color=currentGrid[row][col].color;
        if (currentGrid[row][col+1].color===color && currentGrid[row][col+2].color===color) {
          matches.add(`${row}-${col}`); matches.add(`${row}-${col+1}`); matches.add(`${row}-${col+2}`);
        }
      }
    }
    for (let col=0; col<gridSize; col++) {
      for (let row=0; row<gridSize-2; row++) {
        const color=currentGrid[row][col].color;
        if (currentGrid[row+1][col].color===color && currentGrid[row+2][col].color===color) {
          matches.add(`${row}-${col}`); matches.add(`${row+1}-${col}`); matches.add(`${row+2}-${col}`);
        }
      }
    }
    return Array.from(matches).map(pos => { const [row,col]=pos.split('-').map(Number); return {row,col}; });
  };

  const initializeGrid = () => {
    let newGrid = [];
    do {
      newGrid = [];
      for (let row=0; row<gridSize; row++) {
        newGrid[row]=[];
        for (let col=0; col<gridSize; col++) {
          newGrid[row][col]={ color:getRandomColor(), id:`${row}-${col}-${Date.now()}-${Math.random()}` };
        }
      }
    } while (findMatches(newGrid).length>0);
    setGrid(newGrid); setScore(0); setMoves(30); setSelectedCell(null); setNextColor(getRandomColor());
  };

  const applyGravity = useCallback((currentGrid) => {
    for (let col=0; col<gridSize; col++) {
      let emptySpaces=0;
      for (let row=gridSize-1; row>=0; row--) {
        if (currentGrid[row][col]===null) emptySpaces++;
        else if (emptySpaces>0) { currentGrid[row+emptySpaces][col]=currentGrid[row][col]; currentGrid[row][col]=null; }
      }
      for (let row=0; row<emptySpaces; row++) {
        currentGrid[row][col]={ color:getRandomColor(), id:`${row}-${col}-${Date.now()}-${Math.random()}` };
      }
    }
    setGrid([...currentGrid]);
    setNextColor(getRandomColor());
    setTimeout(() => {
      const newMatches=findMatches(currentGrid);
      if (newMatches.length>0) { playCrushSound(); processMatches(currentGrid, newMatches); }
      else setAnimating(false);
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score, bestScore]);

  const processMatches = useCallback((currentGrid, matches) => {
    const points=matches.length*10;
    const newScore=score+points;
    setScore(newScore);
    if (newScore>bestScore) { setBestScore(newScore); localStorage.setItem('colorcrush_best', newScore.toString()); }
    matches.forEach(({row,col}) => { currentGrid[row][col]=null; });
    setGrid([...currentGrid]);
    setTimeout(() => applyGravity(currentGrid), 300);
  }, [score, bestScore, applyGravity]);

  const swapCells = (row1, col1, row2, col2) => {
    setAnimating(true);
    const newGrid=grid.map(row=>[...row]);
    const temp=newGrid[row1][col1];
    newGrid[row1][col1]=newGrid[row2][col2];
    newGrid[row2][col2]=temp;
    setGrid(newGrid);
    setTimeout(() => {
      const matches=findMatches(newGrid);
      if (matches.length>0) { playCrushSound(); setMoves(prev=>prev-1); setSelectedCell(null); processMatches(newGrid,matches); }
      else { const t=newGrid[row1][col1]; newGrid[row1][col1]=newGrid[row2][col2]; newGrid[row2][col2]=t; setGrid(newGrid); setSelectedCell(null); setAnimating(false); }
    }, 200);
  };

  const handleCellClick = (row, col) => {
    if (moves<=0||animating) return;
    if (!selectedCell) { setSelectedCell({row,col}); }
    else {
      const rowDiff=Math.abs(selectedCell.row-row), colDiff=Math.abs(selectedCell.col-col);
      if ((rowDiff===1&&colDiff===0)||(rowDiff===0&&colDiff===1)) swapCells(selectedCell.row, selectedCell.col, row, col);
      else setSelectedCell({row,col});
    }
  };

  return (
    <div style={{border:'2px dotted #4169E1', padding:'24px', background:'white'}}>
      <div style={{marginBottom:'24px', display:'flex', justifyContent:'center', alignItems:'center', gap:'20px', flexWrap:'wrap'}}>
        <div style={{padding:'16px 24px', background:'#F0F4FF', border:'2px solid #4169E1', minWidth:'120px', textAlign:'center'}}>
          <p className="mono" style={{fontSize:'10px', color:'#666', marginBottom:'4px', letterSpacing:'0.1em'}}>SCORE</p>
          <p className="mono" style={{fontSize:'32px', fontWeight:'600', color:'#4169E1'}}>{score}</p>
        </div>
        {nextColor && (
          <div style={{padding:'16px', background:'#F0F4FF', border:'2px solid #4169E1', textAlign:'center'}}>
            <p className="mono" style={{fontSize:'10px', color:'#666', marginBottom:'8px', letterSpacing:'0.1em'}}>NEXT COLOR</p>
            <div style={{width:'48px', height:'48px', background:nextColor, borderRadius:'8px', margin:'0 auto', boxShadow:'0 2px 8px rgba(0,0,0,0.15)'}} />
          </div>
        )}
        <div style={{padding:'16px 24px', background:'#F0F4FF', border:'2px solid #4169E1', minWidth:'120px', textAlign:'center'}}>
          <p className="mono" style={{fontSize:'10px', color:'#666', marginBottom:'4px', letterSpacing:'0.1em'}}>BEST</p>
          <p className="mono" style={{fontSize:'32px', fontWeight:'600', color:'#4169E1'}}>{bestScore}</p>
        </div>
      </div>
      <div style={{marginBottom:'24px', textAlign:'center'}}>
        <p className="mono" style={{fontSize:'14px', color:moves<5?'#FF6B6B':'#666', fontWeight:moves<5?'600':'400'}}>MOVES REMAINING: {moves}</p>
      </div>
      <div style={{display:'grid', gridTemplateColumns:`repeat(${gridSize}, 1fr)`, gap:'6px', marginBottom:'24px', background:'#E5E7EB', padding:'12px', borderRadius:'12px', maxWidth:'600px', margin:'0 auto 24px auto'}}>
        {grid.map((row,rowIdx) => row.map((cell,colIdx) => (
          <div key={cell?.id||`${rowIdx}-${colIdx}`} onClick={() => handleCellClick(rowIdx,colIdx)}
            style={{aspectRatio:'1', width:'100%', background:cell?cell.color:'#f0f0f0', border:selectedCell?.row===rowIdx&&selectedCell?.col===colIdx?'4px solid #1A1A1A':'none', cursor:moves>0&&!animating&&cell?'pointer':'default', transition:'all 0.2s ease', borderRadius:'12px', boxShadow:cell?'0 2px 4px rgba(0,0,0,0.1)':'none', transform:selectedCell?.row===rowIdx&&selectedCell?.col===colIdx?'scale(0.9)':'scale(1)', opacity:cell?1:0.5}}
            onMouseEnter={(e)=>{ if(moves>0&&!animating&&cell){e.currentTarget.style.transform='scale(1.05)';e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.2)';} }}
            onMouseLeave={(e)=>{ if(selectedCell?.row!==rowIdx||selectedCell?.col!==colIdx){e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow=cell?'0 2px 4px rgba(0,0,0,0.1)':'none';} }}
          />
        )))}
      </div>
      <div style={{textAlign:'center'}}>
        <button className="mono" onClick={initializeGrid} style={{padding:'12px 32px', fontSize:'11px', color:'#FFFFFF', background:'#4169E1', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em', fontWeight:'600'}}>NEW GAME</button>
      </div>
      {moves===0 && (
        <div style={{marginTop:'24px', textAlign:'center', padding:'20px', background:score>bestScore?'#E8F5E9':'#FFE8E8', border:`2px solid ${score>bestScore?'#4CAF50':'#FF6B6B'}`, borderRadius:'8px'}}>
          <p className="mono" style={{fontSize:'16px', fontWeight:'600', color:score>bestScore?'#2E7D32':'#C44569', marginBottom:'8px'}}>GAME OVER!</p>
          <p className="mono" style={{fontSize:'14px', color:'#666'}}>FINAL SCORE: {score}</p>
          {score>bestScore && <p className="mono" style={{fontSize:'12px', color:'#4CAF50', marginTop:'8px'}}>NEW BEST SCORE!</p>}
        </div>
      )}
    </div>
  );
};

// ==================== GAMES MENU ====================
const GamesMenu = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const games = [
    { id:'bubble-pop', name:'BUBBLE POP', component:BubblePop },
    { id:'sandfall', name:'SANDFALL', component:Sandfall },
    { id:'color-crush', name:'COLOR CRUSH', component:ColorCrush },
  ];
  const SelectedGameComponent = games.find(g=>g.id===selectedGame)?.component;

  return (
    <div>
      <div style={{marginBottom:'32px', display:'flex', gap:'12px', flexWrap:'wrap'}}>
        {games.map(game => (
          <button key={game.id} className="mono" onClick={()=>setSelectedGame(game.id)} style={{padding:'12px 24px', fontSize:'11px', color:selectedGame===game.id?'#FFFFFF':'#1A1A1A', background:selectedGame===game.id?'#4169E1':'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>{game.name}</button>
        ))}
      </div>
      {selectedGame&&SelectedGameComponent ? <SelectedGameComponent /> : (
        <div style={{border:'2px dotted #4169E1', padding:'60px 40px', background:'white', textAlign:'center'}}>
          <p className="mono" style={{fontSize:'13px', color:'#999'}}>[ SELECT A GAME ABOVE ]</p>
        </div>
      )}
    </div>
  );
};

// ==================== MOOD TREND CHART ====================
const MoodTrendChart = () => {
  const { entries } = useEntries();
  const moodScores = { calm:75, happy:100, sad:15, anxious:25, tired:35, stressed:20 };
  const moodColors = { calm:'#2CB67D', happy:'#FFD93D', sad:'#4A90E2', anxious:'#FF6B6B', tired:'#9B8EA6', stressed:'#FF4500' };
  const last7 = Array.from({length:7}, (_,i) => {
    const d=new Date(); d.setDate(d.getDate()-(6-i)); return d;
  });
  const points = last7.map(day => {
    const de = entries.filter(e => new Date(e.timestamp).toDateString()===day.toDateString() && e.mood);
    return { day:day.toLocaleDateString('en',{weekday:'short'}).toUpperCase(), score:de[0]?(moodScores[de[0].mood]??50):null, mood:de[0]?.mood };
  });
  const W=560, H=130, P=36, xStep=(W-P*2)/6;
  const yS = s => H-P-(s/100)*(H-P*2);
  const valid = points.map((p,i) => p.score!==null?[P+i*xStep, yS(p.score)]:null);
  const poly = valid.filter(Boolean).map(([x,y]) => `${x},${y}`).join(' ');

  return (
    <div style={{border:'2px dotted #4169E1', padding:'20px', background:'white', marginBottom:'20px'}}>
      <p className="mono" style={{fontSize:'11px', color:'#4169E1', marginBottom:'14px', letterSpacing:'0.1em'}}>[ 7-DAY MOOD TREND ]</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', overflow:'visible'}}>
        {[25,50,75].map(y => <line key={y} x1={P} x2={W-P} y1={yS(y)} y2={yS(y)} stroke="#E5E7EB" strokeWidth="1" strokeDasharray="4,4"/>)}
        {valid.filter(Boolean).length>1 && <polyline points={poly} fill="none" stroke="#4169E1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
        {points.map((p,i) => p.score!==null
          ? <circle key={i} cx={P+i*xStep} cy={yS(p.score)} r="6" fill={moodColors[p.mood]||'#4169E1'} stroke="white" strokeWidth="2.5"/>
          : <circle key={i} cx={P+i*xStep} cy={H/2} r="3" fill="#E5E7EB"/>
        )}
        {points.map((p,i) => <text key={i} x={P+i*xStep} y={H-2} textAnchor="middle" fontSize="9" fill="#999" fontFamily="IBM Plex Mono">{p.day}</text>)}
      </svg>
      {!points.some(p=>p.score!==null) && (
        <p className="mono" style={{fontSize:'10px', color:'#BBB', textAlign:'center', marginTop:'4px'}}>Complete daily check-ins to see your trend</p>
      )}
    </div>
  );
};

// ==================== VENTING ROOM ====================
const VentingRoom = () => {
  const { addEntry } = useEntries();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const voiceRecognitionRef = useRef(null);
  const [ventText, setVentText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const [showSavedFeedback, setShowSavedFeedback] = useState('');

  // Live analysis state
  const [liveAnalysis, setLiveAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const debouncedText = useDebounce(ventText, 1500);

  // Worry Jar state
  const [worryText, setWorryText] = useState('');
  const [released, setReleased] = useState(false);
  const [worriesCount, setWorriesCount] = useState(() => {
    try { return parseInt(localStorage.getItem('mindful_worries_count') || '0'); } catch { return 0; }
  });

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
    };
  }, []);

  // Live AI analysis effect
  useEffect(() => {
    if (debouncedText.length < 40) { setLiveAnalysis(null); return; }
    const run = async () => {
      setAnalysisLoading(true);
      try {
        const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${GROQ_KEY}` },
          body:JSON.stringify({
            model:'llama-3.1-8b-instant', max_tokens:180, temperature:0.5,
            messages:[{ role:'user', content:`Analyze this journal entry. Respond ONLY with valid JSON no markdown no backticks:\n{"emotion":"one word","color":"#hexcolor","insight":"one warm sentence","suggestion":"one specific action","themes":["2-3 theme keywords"],"positivity":0-100}\n\nEntry: "${debouncedText.slice(0,500)}"` }]
          })
        });
        const data = await res.json();
        const raw = data.choices[0].message.content.replace(/```json|```/g,'').trim();
        setLiveAnalysis(JSON.parse(raw));
      } catch { setLiveAnalysis(null); }
      setAnalysisLoading(false);
    };
    run();
  }, [debouncedText]);

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio:true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setVoiceTranscript('');
      mediaRecorder.ondataavailable = (event) => { if (event.data.size>0) audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type:'audio/webm' });
        setAudioBlob(blob);
        if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true; recognition.interimResults = true; recognition.lang='en-US';
        recognition.onresult = (event) => {
          let transcript='';
          for (let i=0; i<event.results.length; i++) { if (event.results[i].isFinal) transcript+=event.results[i][0].transcript+' '; }
          if (transcript) setVoiceTranscript(prev=>prev+transcript);
        };
        recognition.onerror = (event) => console.error('Voice recognition error:', event.error);
        voiceRecognitionRef.current = recognition;
        recognition.start();
      }
    } catch (error) { console.error('Recording error:', error); alert('Cannot access microphone.'); }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); }
    if (voiceRecognitionRef.current) voiceRecognitionRef.current.stop();
  };

  const playRecording = () => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => { setIsPlaying(false); URL.revokeObjectURL(audioUrl); };
      audio.play(); setIsPlaying(true);
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime=0; setIsPlaying(false); }
  };

  const saveVoiceRecording = () => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      addEntry({ type:'voice', content:voiceTranscript||'[Voice recording - transcript not available]', audioUrl, audioBlob, hasAudio:true });
      setShowSavedFeedback('voice');
      setTimeout(()=>setShowSavedFeedback(''), 2000);
    }
  };

  const startSpeechToText = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported.'); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = true; recognition.interimResults = true; recognition.lang='en-US';
    recognition.onresult = (event) => {
      let transcript='';
      for (let i=event.resultIndex; i<event.results.length; i++) { if (event.results[i].isFinal) transcript+=event.results[i][0].transcript+' '; }
      if (transcript) setVentText(prev=>prev+transcript);
    };
    recognition.onerror = (event) => { console.error('Speech error:', event.error); setIsListening(false); };
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start(); setIsListening(true);
  };

  const stopSpeechToText = () => {
    if (recognitionRef.current) { recognitionRef.current.stop(); setIsListening(false); }
  };

  const saveText = () => {
    if (ventText.trim()) {
      addEntry({ type:'vent', content:ventText });
      setShowSavedFeedback('text');
      setTimeout(()=>setShowSavedFeedback(''), 2000);
    }
  };

  const clearAll = () => {
    setVentText(''); setAudioBlob(null); setVoiceTranscript('');
    if (isPlaying) stopPlayback();
  };

  const releaseWorry = () => {
    if (!worryText.trim()) return;
    setReleased(true);
    setTimeout(() => {
      setWorryText(''); setReleased(false);
      const newCount = worriesCount + 1;
      setWorriesCount(newCount);
      try { localStorage.setItem('mindful_worries_count', newCount.toString()); } catch {}
    }, 1300);
  };

  return (
    <div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'24px', marginBottom:'32px'}}>
        {/* VOICE RECORDER */}
        <div style={{border:'2px dotted #4169E1', padding:'24px', background:'white'}}>
          <div style={{textAlign:'center', marginBottom:'24px'}}>
            <div style={{width:'80px', height:'80px', margin:'0 auto 24px', borderRadius:'50%', background:isRecording?'#FF6B6B':'#4169E1', display:'flex', alignItems:'center', justifyContent:'center', animation:isRecording?'breathe 2s infinite ease-in-out':'none'}}>
              <Mic size={32} color="white" strokeWidth={2} />
            </div>
            {isRecording && <p className="mono recording-pulse" style={{fontSize:'11px', color:'#FF6B6B', marginBottom:'16px'}}>● RECORDING...</p>}
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
            {!isRecording ? (
              <button className="mono" onClick={startVoiceRecording} style={{padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#4169E1', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>● START RECORDING</button>
            ) : (
              <button className="mono" onClick={stopVoiceRecording} style={{padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#FF6B6B', border:'2px solid #FF6B6B', cursor:'pointer', letterSpacing:'0.1em'}}>■ STOP</button>
            )}
            {audioBlob && !isRecording && (
              <>
                {!isPlaying ? (
                  <button className="mono" onClick={playRecording} style={{padding:'12px 24px', fontSize:'11px', color:'#1A1A1A', background:'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>▶ PLAY RECORDING</button>
                ) : (
                  <button className="mono" onClick={stopPlayback} style={{padding:'12px 24px', fontSize:'11px', color:'#1A1A1A', background:'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>■ STOP PLAYBACK</button>
                )}
                <button className="mono" onClick={saveVoiceRecording} style={{padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#4169E1', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>SAVE TO ARCHIVE</button>
              </>
            )}
          </div>
          {showSavedFeedback==='voice' && <p className="mono fade-in" style={{fontSize:'10px', color:'#2CB67D', marginTop:'12px', letterSpacing:'0.1em'}}>✓ VOICE RECORDING SAVED</p>}
          {voiceTranscript && (
            <div style={{marginTop:'16px', padding:'12px', background:'#F8F9FA', border:'1px solid #E5E7EB'}}>
              <p className="mono" style={{fontSize:'9px', color:'#666', marginBottom:'8px'}}>TRANSCRIPT (FOR ANALYSIS):</p>
              <p className="mono" style={{fontSize:'11px', color:'#333', lineHeight:'1.6'}}>{voiceTranscript}</p>
            </div>
          )}
        </div>

        {/* TEXT + SPEECH-TO-TEXT */}
        <div style={{border:'2px dotted #4169E1', padding:'24px', background:'white'}}>
          <div style={{position:'relative', marginBottom:'16px'}}>
            <textarea className="mono" value={ventText} onChange={(e)=>setVentText(e.target.value)} placeholder="Type or speak your thoughts..."
              style={{width:'100%', minHeight:'200px', padding:'16px', paddingBottom:'50px', fontSize:'13px', lineHeight:'1.8', color:'#1A1A1A', background:'white', border:'2px solid #4169E1', outline:'none', resize:'vertical'}}
            />
            <button className="mono" onMouseDown={startSpeechToText} onMouseUp={stopSpeechToText} onMouseLeave={()=>isListening&&stopSpeechToText()} onTouchStart={startSpeechToText} onTouchEnd={stopSpeechToText}
              style={{position:'absolute', bottom:'12px', right:'12px', padding:'10px', background:isListening?'#FF6B6B':'#4169E1', border:'none', borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', animation:isListening?'pulse 1.5s ease-in-out infinite':'none', width:'40px', height:'40px'}}
              title="Hold to speak">
              <Mic size={20} color="white" strokeWidth={2} />
            </button>
          </div>

          {isListening && <p className="mono recording-pulse" style={{fontSize:'10px', color:'#FF6B6B', marginBottom:'12px'}}>● LISTENING...</p>}

          {/* Live AI Analysis Panel */}
          <div style={{border:'2px dotted #E5E7EB', background:'#FAFAFA', padding:'14px', marginBottom:'12px'}}>
            <p className="mono" style={{fontSize:'9px', color:'#4169E1', marginBottom:'8px', letterSpacing:'0.05em'}}>[ LIVE ANALYSIS ]</p>
            {ventText.length < 40 && (
              <p className="mono" style={{fontSize:'10px', color:'#999'}}>Write 40+ characters to see live emotional insight</p>
            )}
            {ventText.length >= 40 && analysisLoading && (
              <p className="mono" style={{fontSize:'10px', color:'#4169E1', animation:'thinkingPulse 1.2s ease-in-out infinite'}}>[ ANALYZING... ]</p>
            )}
            {ventText.length >= 40 && !analysisLoading && liveAnalysis && (
              <div>
                <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', flexWrap:'wrap'}}>
                  <div style={{width:'12px', height:'12px', borderRadius:'50%', background:liveAnalysis.color, flexShrink:0}} />
                  <span className="mono" style={{fontSize:'11px', fontWeight:'600'}}>{(liveAnalysis.emotion||'').toUpperCase()}</span>
                  {(liveAnalysis.themes||[]).map((t,i) => (
                    <span key={i} className="mono" style={{fontSize:'9px', background:'#F0F4FF', border:'1px solid #4169E1', padding:'2px 6px', color:'#4169E1'}}>{t}</span>
                  ))}
                </div>
                <div style={{height:'5px', background:'#E5E7EB', marginBottom:'8px', borderRadius:'2px'}}>
                  <div style={{height:'100%', width:`${liveAnalysis.positivity||0}%`, background:liveAnalysis.color, borderRadius:'2px', transition:'width 0.5s ease'}} />
                </div>
                <p className="mono" style={{fontSize:'11px', color:'#555', lineHeight:'1.6', marginBottom:'8px'}}>{liveAnalysis.insight}</p>
                {liveAnalysis.suggestion && (
                  <div style={{background:'#FFFBEB', border:'1px solid #FFD93D', padding:'8px 10px'}}>
                    <p className="mono" style={{fontSize:'9px', color:'#D97706', fontWeight:'600', marginBottom:'4px'}}> SUGGESTION</p>
                    <p className="mono" style={{fontSize:'10px', color:'#333'}}>{liveAnalysis.suggestion}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="mono" onClick={saveText} disabled={!ventText.trim()} style={{width:'100%', padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:ventText.trim()?'#4169E1':'#E5E7EB', border:`2px solid ${ventText.trim()?'#4169E1':'#E5E7EB'}`, cursor:ventText.trim()?'pointer':'not-allowed', letterSpacing:'0.1em'}}>SAVE TO ARCHIVE</button>
          {showSavedFeedback==='text' && <p className="mono fade-in" style={{fontSize:'10px', color:'#2CB67D', marginTop:'8px', letterSpacing:'0.1em'}}>✓ TEXT SAVED TO ARCHIVE</p>}
        </div>
      </div>

      {/* WORRY JAR */}
      <style>{`
        @keyframes worryFly {
          0%   { opacity: 1; transform: translateY(0px) scale(1); filter: blur(0px); }
          60%  { opacity: 0.6; transform: translateY(-60px) scale(0.95); filter: blur(1px); }
          100% { opacity: 0; transform: translateY(-140px) scale(0.8); filter: blur(4px); }
        }
        .worry-fly { animation: worryFly 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
      `}</style>
      <div style={{border:'2px dotted #4169E1', background:'white', padding:'24px', marginBottom:'32px'}}>
        <p className="mono" style={{fontSize:'11px', color:'#4169E1', marginBottom:'6px', letterSpacing:'0.1em'}}>[ WORRY JAR ]</p>
        <p className="mono" style={{fontSize:'11px', color:'#999', marginBottom:'16px'}}>Write a worry. Release it.</p>
        <div style={{position:'relative', minHeight:'50px', marginBottom:'12px'}}>
          {released && (
            <p className="mono worry-fly" style={{fontSize:'13px', color:'#4169E1', position:'absolute', top:'12px', left:'16px', right:'16px', pointerEvents:'none', zIndex:10}}>
              {worryText}
            </p>
          )}
          <input className="mono" value={released ? '' : worryText} onChange={(e)=>setWorryText(e.target.value)} placeholder="What's worrying you right now?"
            onKeyDown={(e)=>{ if(e.key==='Enter') releaseWorry(); }}
            style={{width:'100%', padding:'12px 16px', fontSize:'13px', border:'2px solid #4169E1', outline:'none', background:'white'}}
          />
        </div>
        <button className="mono" onClick={releaseWorry} disabled={!worryText.trim()||released}
          style={{width:'100%', padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#1549e4', border:'none', cursor:worryText.trim()&&!released?'pointer':'not-allowed', letterSpacing:'0.1em', opacity:worryText.trim()&&!released?1:0.5}}>
          RELEASE THIS WORRY →
        </button>
        {worriesCount > 0 && <p className="mono" style={{fontSize:'10px', color:'#999', marginTop:'10px'}}>WORRIES RELEASED: {worriesCount}</p>}
      </div>

      {/* CLEAR BUTTON */}
      <div style={{display:'flex', gap:'12px', marginBottom:'32px'}}>
        <button className="mono" onClick={clearAll} disabled={!ventText.trim()&&!audioBlob}
          style={{padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#FF6B6B', border:'2px solid #FF6B6B', cursor:(ventText.trim()||audioBlob)?'pointer':'not-allowed', opacity:(ventText.trim()||audioBlob)?1:0.4, letterSpacing:'0.1em'}}>
          ✕ CLEAR ALL
        </button>
      </div>

      <div style={{padding:'20px', background:'#FFF9F0', border:'1px solid #FFE8C5'}}>
        <p className="mono" style={{fontSize:'10px', fontWeight:'600', marginBottom:'12px', color:'#D97706', letterSpacing:'0.1em'}}>NEED IMMEDIATE SUPPORT?</p>
        <p className="mono" style={{fontSize:'11px', color:'#666'}}>Emergency: 112 | KIRAN: 1800-599-0019 | Prevention: 096111 94949</p>
      </div>
    </div>
  );
};

// ==================== ARCHIVE ====================
const Archive = () => {
  const { entries } = useEntries();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [analyzingEntry, setAnalyzingEntry] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);

  const getDaysInMonth = (date) => {
    const year=date.getFullYear(), month=date.getMonth();
    const firstDay=new Date(year,month,1), lastDay=new Date(year,month+1,0);
    const daysInMonth=lastDay.getDate(), startingDayOfWeek=firstDay.getDay();
    const days=[];
    for (let i=0; i<startingDayOfWeek; i++) days.push(null);
    for (let i=1; i<=daysInMonth; i++) days.push(new Date(year,month,i));
    return days;
  };

  const getEntriesForDate = (date) => {
    if (!date) return [];
    return entries.filter(entry => {
      const ed=new Date(entry.timestamp);
      return ed.getDate()===date.getDate() && ed.getMonth()===date.getMonth() && ed.getFullYear()===date.getFullYear();
    });
  };

  const hasEntriesOnDate = (date) => getEntriesForDate(date).length > 0;

  const analyzeText = (text) => {
    if (!text||text.trim().length===0) return null;
    const lowerText=text.toLowerCase();
    const wordCount=text.split(/\s+/).length;
    const keywordPatterns = {
      work:['work','job','boss','colleague','deadline','meeting','project','career'],
      relationships:['friend','family','partner','relationship','love','alone','lonely'],
      health:['sleep','tired','sick','pain','energy','exercise','health'],
      emotions:['anxious','stressed','happy','sad','angry','worried','excited'],
      growth:['learn','grow','change','improve','goal','success','progress']
    };
    const detectedCategories = {};
    let maxCategory=null, maxCount=0;
    Object.keys(keywordPatterns).forEach(category => {
      const count=keywordPatterns[category].reduce((acc,keyword) => {
        const regex=new RegExp('\\b'+keyword+'\\b','gi');
        return acc+(lowerText.match(regex)||[]).length;
      }, 0);
      detectedCategories[category]=count;
      if (count>maxCount) { maxCount=count; maxCategory=category; }
    });
    const suggestions = {
      work:"Consider setting clear work boundaries. Take regular breaks and prioritize tasks that align with your goals.",
      relationships:"Nurture your connections. Reach out to someone you trust. Quality time with loved ones can be healing.",
      health:"Your physical wellbeing matters. Focus on consistent sleep, gentle movement, and nourishing foods.",
      emotions:"Your feelings are valid. Try journaling more, practicing mindfulness, or speaking with a counselor.",
      growth:"You're on a growth journey. Celebrate small wins and be patient with yourself. Progress isn't always linear."
    };
    const emotions = {
      anger:{ keywords:['angry','mad','furious','hate','frustrated'], intensity:0, color:'#FF6B6B' },
      sadness:{ keywords:['sad','depressed','hopeless','lonely','hurt'], intensity:0, color:'#4A90E2' },
      anxiety:{ keywords:['anxious','worried','scared','nervous','panic'], intensity:0, color:'#FFD93D' },
      relief:{ keywords:['better','relief','calm','grateful','peaceful','happy'], intensity:0, color:'#95E1D3' },
    };
    Object.keys(emotions).forEach(emotion => {
      emotions[emotion].keywords.forEach(keyword => { if (lowerText.includes(keyword)) emotions[emotion].intensity+=2; });
    });
    const sortedEmotions=Object.entries(emotions).map(([name,data])=>({name,...data})).sort((a,b)=>b.intensity-a.intensity).filter(e=>e.intensity>0);
    const dominantEmotion=sortedEmotions.length>0?sortedEmotions[0].name:'neutral';
    return {
      dominant:dominantEmotion, dominantColor:sortedEmotions.length>0?sortedEmotions[0].color:'#4169E1',
      message:`Detected focus: ${maxCategory||'general reflection'}`,
      aiSuggestion:maxCategory?suggestions[maxCategory]:"Keep reflecting. Self-awareness is the first step to growth.",
      detectedCategories, wordCount, isAI:false
    };
  };

  const handleAnalyze = async (entry) => {
    setAnalyzingEntry(entry.id);
    setAnalysis(null);
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${GROQ_KEY}` },
        body:JSON.stringify({
          model:'llama-3.1-8b-instant', max_tokens:220, temperature:0.6,
          messages:[{ role:'user', content:`Analyze this journal entry. Respond ONLY with valid JSON, no markdown, no backticks:\n{"emotion":"one word","color":"#hexcolor matching the emotion","insight":"2 warm supportive sentences","suggestion":"one specific actionable thing to do today","positivity":0-100}\n\nEntry: "${(entry.content||'').slice(0,600)}"` }]
        })
      });
      const data = await res.json();
      const raw = data.choices[0].message.content.replace(/```json|```/g,'').trim();
      const result = JSON.parse(raw);
      setAnalysis({ dominant:result.emotion, dominantColor:result.color, message:result.insight, aiSuggestion:result.suggestion, positivity:result.positivity, isAI:true });
    } catch {
      setAnalysis(analyzeText(entry.content));
    }
  };

  const playAudio = (entry) => {
    if (entry.audioUrl) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current=null; }
      const audio=new Audio(entry.audioUrl);
      audioRef.current=audio;
      audio.onended=()=>{ setPlayingAudio(null); audioRef.current=null; };
      audio.play(); setPlayingAudio(entry.id);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime=0; audioRef.current=null; }
    setPlayingAudio(null);
  };

  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current=null; } };
  }, []);

  const exportJournal = () => {
    const lines = entries.map(e => {
      const date=new Date(e.timestamp).toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric',hour:'2-digit',minute:'2-digit'});
      const moodLine = e.mood ? `\nMood: ${e.mood.toUpperCase()}` : '';
      return `---\n${date}\nType: ${e.type?.toUpperCase()||'ENTRY'}${moodLine}\n\n${e.content||''}\n`;
    }).join('\n');
    const header=`MINDFUL — MY JOURNAL EXPORT\nGenerated: ${new Date().toLocaleDateString()}\nTotal entries: ${entries.length}\n\n`;
    const blob=new Blob([header+lines],{type:'text/plain'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url; a.download=`mindful-journal-${new Date().toISOString().split('T')[0]}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});
  const changeMonth = (direction) => { const nd=new Date(currentDate); nd.setMonth(nd.getMonth()+direction); setCurrentDate(nd); setSelectedDate(null); };

  const days=getDaysInMonth(currentDate);
  const monthYear=currentDate.toLocaleString('default',{month:'long',year:'numeric'});
  const selectedEntries=selectedDate?getEntriesForDate(selectedDate):[];

  return (
    <div style={{maxWidth:'1200px', margin:'0 auto'}}>
      <div className="fade-in" style={{marginBottom:'32px'}}>
        <h2 className="mono" style={{fontSize:'24px', fontWeight:'600', color:'#4169E1', marginBottom:'16px'}}>ARCHIVE</h2>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px', padding:'16px 20px', background:'#F0F4FF', border:'2px dotted #4169E1'}}>
          <p className="mono" style={{fontSize:'11px', color:'#666'}}>Total Entries: {entries.length}</p>
          <button className="mono" onClick={exportJournal} style={{padding:'8px 16px', fontSize:'10px', color:'#4169E1', background:'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>⬇ EXPORT JOURNAL</button>
        </div>
      </div>

      <div className="fade-in" style={{marginBottom:'32px', border:'2px dotted #4169E1', padding:'24px', background:'white'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px'}}>
          <button className="mono" onClick={()=>changeMonth(-1)} style={{padding:'8px 16px', fontSize:'11px', background:'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}><ChevronLeft size={16} /></button>
          <p className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#4169E1', letterSpacing:'0.1em'}}>{monthYear.toUpperCase()}</p>
          <button className="mono" onClick={()=>changeMonth(1)} style={{padding:'8px 16px', fontSize:'11px', background:'white', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}><ChevronRight size={16} /></button>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px', marginBottom:'4px'}}>
          {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(day => (
            <div key={day} style={{padding:'8px', textAlign:'center'}}>
              <p className="mono" style={{fontSize:'9px', color:'#666', fontWeight:'600', letterSpacing:'0.1em'}}>{day}</p>
            </div>
          ))}
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px'}}>
          {days.map((day,index) => {
            if (!day) return <div key={`empty-${index}`} style={{padding:'16px'}} />;
            const hasEntries=hasEntriesOnDate(day);
            const isSelected=selectedDate&&day.getDate()===selectedDate.getDate()&&day.getMonth()===selectedDate.getMonth()&&day.getFullYear()===selectedDate.getFullYear();
            const isToday=day.getDate()===new Date().getDate()&&day.getMonth()===new Date().getMonth()&&day.getFullYear()===new Date().getFullYear();
            return (
              <button key={day.toISOString()} onClick={()=>setSelectedDate(day)}
                style={{padding:'16px', background:isSelected?'#4169E1':hasEntries?'#F0F4FF':'white', border:isToday?'2px solid #FF6B6B':'1px solid #DDD', cursor:'pointer', position:'relative', transition:'all 0.2s'}}
                onMouseEnter={(e)=>{ if(!isSelected) e.currentTarget.style.background='#F8F9FA'; }}
                onMouseLeave={(e)=>{ if(!isSelected) e.currentTarget.style.background=hasEntries?'#F0F4FF':'white'; }}>
                <p className="mono" style={{fontSize:'13px', fontWeight:hasEntries?'600':'400', color:isSelected?'#FFFFFF':'#1A1A1A'}}>{day.getDate()}</p>
                {hasEntries&&!isSelected && <div style={{position:'absolute', bottom:'4px', left:'50%', transform:'translateX(-50%)', width:'6px', height:'6px', borderRadius:'50%', background:'#4169E1'}} />}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="fade-in" style={{marginBottom:'32px'}}>
          <div style={{padding:'16px 20px', background:'#F0F4FF', border:'2px dotted #4169E1', marginBottom:'16px'}}>
            <p className="mono" style={{fontSize:'13px', fontWeight:'600', color:'#4169E1'}}>{selectedDate.toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'}).toUpperCase()}</p>
            <p className="mono" style={{fontSize:'10px', color:'#666', marginTop:'4px'}}>{selectedEntries.length} {selectedEntries.length===1?'ENTRY':'ENTRIES'}</p>
          </div>

          {selectedEntries.length===0 ? (
            <div style={{padding:'40px 20px', textAlign:'center', border:'2px dotted #DDD', background:'white'}}>
              <p className="mono" style={{fontSize:'12px', color:'#999'}}>NO ENTRIES FOR THIS DATE</p>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
              {selectedEntries.map(entry => (
                <div key={entry.id} style={{padding:'20px', background:'white', border:'2px dotted #4169E1'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px', flexWrap:'wrap', gap:'8px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                      <div style={{padding:'4px 12px', background:entry.type==='journal'?'#F0F4FF':entry.type==='voice'?'#FFE8E8':'#FFF9F0', border:`1px solid ${entry.type==='journal'?'#4169E1':entry.type==='voice'?'#FF6B6B':'#FFD93D'}`}}>
                        <p className="mono" style={{fontSize:'9px', fontWeight:'600', color:'#666'}}>{entry.type==='journal'?'JOURNAL':entry.type==='voice'?'VOICE':entry.type==='vent'?'VENT':'REFLECTION'}</p>
                      </div>
                      {entry.mood && <div style={{padding:'4px 12px', background:'#F0F4FF', border:'1px solid #4169E1'}}><p className="mono" style={{fontSize:'9px', fontWeight:'600', color:'#4169E1'}}>{entry.mood.toUpperCase()}</p></div>}
                      {entry.hasAudio && <div style={{padding:'4px 8px', background:'#FFE8E8', border:'1px solid #FF6B6B', display:'flex', alignItems:'center', gap:'4px'}}><Volume2 size={12} color="#FF6B6B" /><p className="mono" style={{fontSize:'9px', fontWeight:'600', color:'#FF6B6B'}}>AUDIO</p></div>}
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                      <p className="mono" style={{fontSize:'10px', color:'#999'}}>{formatTime(entry.timestamp)}</p>
                      <button className="mono" onClick={()=>handleAnalyze(entry)}
                        disabled={entry.type==='voice'&&entry.content==='[Voice recording - transcript not available]'}
                        style={{padding:'6px 12px', fontSize:'9px', fontWeight:'600', color:'#FFFFFF', background:(entry.type==='voice'&&entry.content==='[Voice recording - transcript not available]')?'#999':'#4169E1', border:'none', cursor:(entry.type==='voice'&&entry.content==='[Voice recording - transcript not available]')?'not-allowed':'pointer', letterSpacing:'0.1em', opacity:(entry.type==='voice'&&entry.content==='[Voice recording - transcript not available]')?0.5:1}}>
                        ANALYZE
                      </button>
                    </div>
                  </div>

                  <p className="mono" style={{fontSize:'13px', lineHeight:'1.8', color:'#333', marginBottom:'16px'}}>
                    {entry.type==='voice'&&entry.content==='[Voice recording - transcript not available]'
                      ? <span style={{color:'#999', fontStyle:'italic'}}>[Voice recording - transcript not available for analysis]</span>
                      : entry.content}
                  </p>

                  {entry.hasAudio&&entry.audioUrl && (
                    <div style={{marginTop:'16px', padding:'16px', background:'#F8F9FA', border:'1px solid #E5E7EB'}}>
                      <p className="mono" style={{fontSize:'10px', color:'#4169E1', marginBottom:'12px', letterSpacing:'0.1em'}}>[ AUDIO RECORDING ]</p>
                      <div style={{display:'flex', gap:'12px'}}>
                        {playingAudio!==entry.id ? (
                          <button className="mono" onClick={()=>playAudio(entry)} style={{padding:'10px 20px', fontSize:'10px', fontWeight:'600', color:'#FFFFFF', background:'#4169E1', border:'none', cursor:'pointer', letterSpacing:'0.1em', display:'flex', alignItems:'center', gap:'6px'}}><Play size={14} />PLAY RECORDING</button>
                        ) : (
                          <button className="mono" onClick={stopAudio} style={{padding:'10px 20px', fontSize:'10px', fontWeight:'600', color:'#FFFFFF', background:'#FF6B6B', border:'none', cursor:'pointer', letterSpacing:'0.1em', display:'flex', alignItems:'center', gap:'6px'}}><Square size={14} />STOP</button>
                        )}
                      </div>
                    </div>
                  )}

                  {analyzingEntry===entry.id && analysis && (
                    <div style={{marginTop:'16px', padding:'16px', background:'#F8F9FA', border:'1px solid #E5E7EB'}}>
                      <p className="mono" style={{fontSize:'10px', color:'#4169E1', marginBottom:'8px', letterSpacing:'0.1em'}}>
                        {analysis.isAI ? ' AI ANALYSIS ' : ' KEYWORD ANALYSIS'}
                      </p>
                      <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px'}}>
                        {analysis.dominantColor && <div style={{width:'16px', height:'16px', borderRadius:'50%', background:analysis.dominantColor}} />}
                        <p className="mono" style={{fontSize:'12px', fontWeight:'600'}}>{(analysis.dominant||'').toUpperCase()}</p>
                      </div>
                      {analysis.positivity !== undefined && (
                        <div style={{marginBottom:'12px'}}>
                          <p className="mono" style={{fontSize:'9px', color:'#666', marginBottom:'4px'}}>POSITIVITY: {analysis.positivity}/100</p>
                          <div style={{height:'5px', background:'#E5E7EB', borderRadius:'2px'}}>
                            <div style={{height:'100%', width:`${analysis.positivity}%`, background:analysis.dominantColor, borderRadius:'2px'}} />
                          </div>
                        </div>
                      )}
                      <p className="mono" style={{fontSize:'11px', color:'#666', marginBottom:'12px'}}>{analysis.message}</p>
                      <div style={{padding:'12px', background:'#FFFBEB', border:'1px solid #FFD93D', marginTop:'12px'}}>
                        <p className="mono" style={{fontSize:'10px', color:'#D97706', fontWeight:'600', marginBottom:'8px', letterSpacing:'0.1em'}}> SUGGESTION:</p>
                        <p className="mono" style={{fontSize:'11px', color:'#333', lineHeight:'1.6'}}>{analysis.aiSuggestion}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== AI COMPANION ====================
const AICompanion = ({ currentMood }) => {
  const [messages, setMessages] = useState([{
    role:'assistant',
    content:"Hello. I'm ARIA — your wellness companion in MINDFUL.\n\nI'm here to listen without judgment. You can share how you're feeling, talk through what's on your mind, or just vent. How are you doing right now?"
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role:'user', content:input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const history = messages.map(m=>({ role:m.role, content:m.content }));
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${GROQ_KEY}` },
        body:JSON.stringify({
          model:'llama-3.1-8b-instant', max_tokens:280, temperature:0.75,
          messages:[
            { role:'system', content:`You are ARIA, a warm compassionate mental wellness companion in the MINDFUL app. Keep responses to 3-5 sentences maximum. Ask one thoughtful follow-up question per response. Always validate feelings before offering perspective. Never diagnose or prescribe. Speak in a grounded, human way. The user's current mood check-in was: ${currentMood||'unknown'}.` },
            ...history,
            { role:'user', content:input }
          ]
        })
      });
      const data = await res.json();
      const reply = data.choices[0].message.content;
      setMessages(prev => [...prev, { role:'assistant', content:reply }]);
    } catch {
      setMessages(prev => [...prev, { role:'assistant', content:"I'm having trouble connecting right now. Please try again in a moment." }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key==='Enter'&&!e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{maxWidth:'800px', margin:'0 auto', display:'flex', flexDirection:'column', height:'calc(100vh - 220px)', minHeight:'500px'}}>
      {/* Header */}
      <div style={{border:'2px dotted #4169E1', padding:'16px 20px', background:'white', marginBottom:'16px', display:'flex', alignItems:'center', gap:'16px'}}>
        <div style={{width:'40px', height:'40px', borderRadius:'50%', background:'#4169E1', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
          <Bot size={22} color="white" strokeWidth={2} />
        </div>
        <div style={{flex:1}}>
          <p className="mono" style={{fontSize:'14px', fontWeight:'700', color:'#4169E1'}}>ARIA</p>
          <p className="mono" style={{fontSize:'9px', color:'#999'}}>Adaptive Reflective Intelligence for Awareness</p>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
          <div style={{width:'8px', height:'8px', borderRadius:'50%', background:'#2CB67D'}} />
          <p className="mono" style={{fontSize:'9px', color:'#2CB67D', fontWeight:'600'}}>ONLINE</p>
        </div>
      </div>

      {/* Message window */}
      <div style={{flex:1, border:'2px dotted #4169E1', overflowY:'auto', padding:'20px', display:'flex', flexDirection:'column', gap:'16px', background:'white'}}>
        {messages.map((msg, i) => (
          <div key={i} style={{display:'flex', flexDirection:msg.role==='user'?'row-reverse':'row', gap:'12px', alignItems:'flex-start'}}>
            <div style={{width:'32px', height:'32px', borderRadius:'50%', flexShrink:0, background:msg.role==='user'?'white':'#4169E1', border:msg.role==='user'?'2px solid #4169E1':'none', display:'flex', alignItems:'center', justifyContent:'center'}}>
              {msg.role==='user' ? <User size={16} color="#4169E1" strokeWidth={2} /> : <Bot size={16} color="white" strokeWidth={2} />}
            </div>
            <div style={{maxWidth:'70%', padding:'12px 16px', background:msg.role==='user'?'white':'#F0F4FF', border:msg.role==='user'?'2px dotted #4169E1':'2px solid #4169E1'}}>
              <p className="mono" style={{fontSize:'12px', lineHeight:'1.9', color:'#1A1A1A', whiteSpace:'pre-wrap'}}>{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{display:'flex', flexDirection:'row', gap:'12px', alignItems:'flex-start'}}>
            <div style={{width:'32px', height:'32px', borderRadius:'50%', flexShrink:0, background:'#4169E1', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <Bot size={16} color="white" strokeWidth={2} />
            </div>
            <div style={{maxWidth:'70%', padding:'12px 16px', background:'#F0F4FF', border:'2px solid #4169E1'}}>
              <p className="mono" style={{fontSize:'12px', color:'#4169E1', animation:'thinkingPulse 1.2s ease-in-out infinite'}}>ARIA IS THINKING...</p>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{border:'2px dotted #4169E1', background:'white', marginTop:'12px', display:'flex', alignItems:'flex-end', gap:'0'}}>
        <textarea className="mono" value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder="Share what's on your mind..."
          style={{flex:1, padding:'14px 16px', fontSize:'12px', lineHeight:'1.6', border:'none', outline:'none', resize:'none', minHeight:'52px', maxHeight:'120px', background:'transparent'}}
          rows={1}
        />
        <button onClick={sendMessage} disabled={!input.trim()||loading}
          style={{padding:'14px 20px', background:input.trim()&&!loading?'#4169E1':'#E5E7EB', border:'none', cursor:input.trim()&&!loading?'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', alignSelf:'stretch'}}>
          <Send size={18} color={input.trim()&&!loading?'white':'#999'} strokeWidth={2} />
        </button>
      </div>

      {/* Disclaimer */}
      <p className="mono" style={{fontSize:'9px', color:'#BBB', textAlign:'center', marginTop:'8px'}}>
        ARIA is an AI support tool, not a therapist. Crisis? Call KIRAN: 1800-599-0019
      </p>
    </div>
  );
};

// ==================== HOME PAGE ====================
const HomePage = ({ currentMood }) => {
  const { entries, addEntry } = useEntries();
  const [morningHabits, setMorningHabits] = useState([
    { id:1, label:'HYDRATION CHECK', completed:false },
    { id:2, label:'BREATHING EXERCISE', completed:false },
    { id:3, label:'MOVEMENT PROTOCOL', completed:false },
    { id:4, label:'NUTRITION INTAKE', completed:false },
  ]);
  const [eveningHabits, setEveningHabits] = useState([
    { id:5, label:'SCREEN DISCONNECT', completed:false },
    { id:6, label:'REFLECTION LOG', completed:false },
    { id:7, label:'GRATITUDE NOTE', completed:false },
    { id:8, label:'SLEEP PREPARATION', completed:false },
  ]);
  const [reflectionText, setReflectionText] = useState('');
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const streak = getStreak(entries);

  const toggleHabit = (id, isMorning) => {
    if (isMorning) setMorningHabits(morningHabits.map(h=>h.id===id?{...h,completed:!h.completed}:h));
    else setEveningHabits(eveningHabits.map(h=>h.id===id?{...h,completed:!h.completed}:h));
  };

  const handleSaveReflection = () => {
    if (reflectionText.trim()) {
      addEntry({ type:'reflection', content:reflectionText, mood:currentMood });
      setReflectionText('');
      setShowSavedFeedback(true);
      setTimeout(()=>setShowSavedFeedback(false), 2000);
    }
  };

  const generateWeeklyReport = async () => {
    setReportLoading(true);
    const weekAgo=new Date(); weekAgo.setDate(weekAgo.getDate()-7);
    const weekEntries=entries.filter(e=>new Date(e.timestamp)>=weekAgo);
    const moodList=weekEntries.filter(e=>e.mood).map(e=>e.mood).join(', ')||'no check-ins';
    const entryTexts=weekEntries.slice(0,8).map(e=>`[${e.type}]: ${(e.content||'').slice(0,150)}`).join('\n');
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${GROQ_KEY}` },
        body:JSON.stringify({
          model:'llama-3.1-8b-instant', max_tokens:500, temperature:0.7,
          messages:[{ role:'user', content:`Generate a weekly wellness report. Respond ONLY with valid JSON no markdown no backticks:\n{"headline":"punchy title for their week in 5-7 words","score":0-100,"pattern":"2 sentences about emotional arc this week","strengths":["2 specific observed strengths"],"focus":["2 areas needing attention"],"recommendations":[{"title":"short title","detail":"one specific sentence"},{"title":"short title","detail":"one specific sentence"},{"title":"short title","detail":"one specific sentence"}],"affirmation":"one personalized specific affirmation for next week"}\n\nMood check-ins this week: ${moodList}\nJournal entries:\n${entryTexts}` }]
        })
      });
      const data = await res.json();
      const raw = data.choices[0].message.content.replace(/```json|```/g,'').trim();
      setReport(JSON.parse(raw));
      setReportOpen(true);
    } catch { setReport(null); }
    setReportLoading(false);
  };

  const morningProgress=(morningHabits.filter(h=>h.completed).length/morningHabits.length)*100;
  const eveningProgress=(eveningHabits.filter(h=>h.completed).length/eveningHabits.length)*100;
  const totalProgress=(morningProgress+eveningProgress)/2;
  const quotes=["Your mental health is a priority, not an afterthought.","Small steps forward are still progress.","You are allowed to be both a masterpiece and a work in progress."];
  const dailyQuote=quotes[new Date().getDate()%quotes.length];
  const getCurrentDate = () => {
    const days=['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
    const months=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const now=new Date();
    return `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
  };
  const getGreeting = () => {
    const hour=new Date().getHours();
    if (hour<12) return 'GOOD MORNING';
    if (hour<18) return 'GOOD AFTERNOON';
    return 'GOOD EVENING';
  };

  return (
    <div style={{maxWidth:'1000px', margin:'0 auto'}}>
      <div className="fade-in" style={{marginBottom:'40px'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'16px', marginBottom:'12px'}}>
          <div>
            <h1 className="mono" style={{fontSize:'clamp(24px, 5vw, 36px)', fontWeight:'600', color:'#4169E1', letterSpacing:'0.05em', marginBottom:'8px'}}>{getGreeting()}</h1>
            <p className="mono" style={{fontSize:'12px', color:'#666', letterSpacing:'0.1em'}}>{getCurrentDate()}</p>
          </div>
          <div style={{display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'flex-start'}}>
            {streak > 0 && (
              <div style={{padding:'10px 18px', background:'#FFF9F0', border:'2px solid #FFD93D'}}>
                <p className="mono" style={{fontSize:'9px', color:'#D97706', letterSpacing:'0.1em', marginBottom:'2px'}}> STREAK</p>
                <p className="mono" style={{fontSize:'20px', fontWeight:'700', color:'#D97706'}}>{streak}D</p>
              </div>
            )}
            {currentMood && (
              <div style={{padding:'8px 16px', background:'#F0F4FF', border:'2px solid #4169E1'}}>
                <p className="mono" style={{fontSize:'10px', color:'#666', marginBottom:'4px', letterSpacing:'0.1em'}}>SYSTEM STATUS</p>
                <p className="mono" style={{fontSize:'13px', fontWeight:'600', color:'#4169E1', letterSpacing:'0.1em'}}>{currentMood.toUpperCase()}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="fade-in" style={{marginBottom:'40px', border:'2px dotted #4169E1', padding:'20px', background:'white'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <p className="mono" style={{fontSize:'11px', color:'#4169E1', letterSpacing:'0.1em'}}>[ DAILY COMPLETION ]</p>
          <p className="mono" style={{fontSize:'13px', fontWeight:'600'}}>{Math.round(totalProgress)}%</p>
        </div>
        <div style={{width:'100%', height:'8px', background:'#E5E7EB', overflow:'hidden'}}>
          <div style={{height:'100%', width:`${totalProgress}%`, background:'#4169E1', transition:'width 0.3s ease'}} />
        </div>
      </div>

      {/* Mood Trend Chart */}
      <MoodTrendChart />

      {/* Weekly Report Button */}
      <div style={{marginBottom:'20px'}}>
        <button className="mono" onClick={generateWeeklyReport} disabled={reportLoading}
          style={{width:'100%', padding:'16px', background:'#3d5ce4', color:'white', border:'none', cursor:reportLoading?'not-allowed':'pointer', fontSize:'12px', letterSpacing:'0.1em', fontWeight:'600', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', opacity:reportLoading?0.7:1}}>
          {reportLoading ? <><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}} /> GENERATING REPORT...</> : '✦ GENERATE WEEKLY REPORT'}
        </button>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* Weekly Report Card */}
      {reportOpen && report && (
        <div className="fade-in" style={{border:'2px dotted #4169E1', background:'white', padding:'28px', marginBottom:'32px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px', flexWrap:'wrap', gap:'12px'}}>
            <p className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#1A1A1A', flex:1}}>{report.headline}</p>
            <div style={{border:'2px solid #4169E1', padding:'8px 16px', textAlign:'center', minWidth:'70px'}}>
              <p className="mono" style={{fontSize:'9px', color:'#666', marginBottom:'2px'}}>SCORE</p>
              <p className="mono" style={{fontSize:'28px', fontWeight:'700', color:'#4169E1'}}>{report.score}</p>
            </div>
          </div>
          <div style={{display:'inline-block', padding:'4px 12px', background:'#1A1A1A', marginBottom:'16px'}}>
            <p className="mono" style={{fontSize:'9px', color:'white', letterSpacing:'0.1em'}}>THIS WEEK'S PATTERN</p>
          </div>
          <p className="mono" style={{fontSize:'12px', color:'#555', lineHeight:'1.8', marginBottom:'20px'}}>{report.pattern}</p>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'12px', marginBottom:'20px'}}>
            <div style={{background:'#F0FFF4', border:'1px solid #4CAF50', padding:'16px'}}>
              <p className="mono" style={{fontSize:'10px', color:'#2E7D32', fontWeight:'600', marginBottom:'8px'}}>✓ STRENGTHS</p>
              {(report.strengths||[]).map((s,i)=><p key={i} className="mono" style={{fontSize:'11px', color:'#333', marginBottom:'4px'}}>• {s}</p>)}
            </div>
            <div style={{background:'#FFFBEB', border:'1px solid #FFD93D', padding:'16px'}}>
              <p className="mono" style={{fontSize:'10px', color:'#D97706', fontWeight:'600', marginBottom:'8px'}}>↗ FOCUS AREAS</p>
              {(report.focus||[]).map((f,i)=><p key={i} className="mono" style={{fontSize:'11px', color:'#333', marginBottom:'4px'}}>• {f}</p>)}
            </div>
          </div>
          <p className="mono" style={{fontSize:'10px', color:'#4169E1', marginBottom:'12px', letterSpacing:'0.1em'}}>RECOMMENDATIONS</p>
          {(report.recommendations||[]).map((r,i)=>(
            <div key={i} style={{display:'flex', gap:'12px', marginBottom:'12px', padding:'12px', border:'1px solid #E5E7EB'}}>
              <span className="mono" style={{fontSize:'12px', fontWeight:'700', color:'#4169E1', flexShrink:0}}>{i+1}.</span>
              <div>
                <p className="mono" style={{fontSize:'11px', fontWeight:'600', marginBottom:'4px'}}>{r.title}</p>
                <p className="mono" style={{fontSize:'11px', color:'#666'}}>{r.detail}</p>
              </div>
            </div>
          ))}
          <div style={{background:'#F0F4FF', border:'1px solid #4169E1', padding:'16px', marginTop:'16px', marginBottom:'16px'}}>
            <p className="mono" style={{fontSize:'11px', fontStyle:'italic', color:'#4169E1', lineHeight:'1.8'}}>{report.affirmation}</p>
          </div>
          <button className="mono" onClick={()=>setReportOpen(false)} style={{padding:'8px 20px', fontSize:'10px', color:'#666', background:'white', border:'1px solid #DDD', cursor:'pointer', letterSpacing:'0.1em'}}>CLOSE REPORT</button>
        </div>
      )}

      <div className="fade-in" style={{marginBottom:'40px', border:'2px solid #4169E1', padding:'32px', background:'white', textAlign:'center'}}>
        <p className="mono" style={{fontSize:'10px', color:'#4169E1', marginBottom:'16px', letterSpacing:'0.15em'}}>[ NOTE ]</p>
        <p className="mono" style={{fontSize:'16px', lineHeight:'1.8', fontStyle:'italic'}}>"{dailyQuote}"</p>
      </div>

      <div className="fade-in" style={{marginBottom:'40px'}}>
        <p className="mono" style={{fontSize:'11px', color:'#4169E1', marginBottom:'16px', letterSpacing:'0.1em'}}>[ MAINTENANCE PROTOCOLS ]</p>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'16px'}}>
          <div style={{border:'2px dotted #4169E1', padding:'20px', background:'white'}}>
            <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px'}}>
              <Sun size={16} color="#FFD93D" strokeWidth={2} />
              <p className="mono" style={{fontSize:'11px', fontWeight:'600', letterSpacing:'0.1em'}}>MORNING PROTOCOL</p>
            </div>
            {morningHabits.map(habit => (
              <label key={habit.id} style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px', cursor:'pointer'}}>
                <input type="checkbox" checked={habit.completed} onChange={()=>toggleHabit(habit.id,true)} style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#4169E1'}} />
                <span className="mono" style={{fontSize:'11px', color:habit.completed?'#999':'#1A1A1A', textDecoration:habit.completed?'line-through':'none'}}>{habit.label}</span>
              </label>
            ))}
          </div>
          <div style={{border:'2px dotted #4169E1', padding:'20px', background:'white'}}>
            <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px'}}>
              <Moon size={16} color="#4169E1" strokeWidth={2} />
              <p className="mono" style={{fontSize:'11px', fontWeight:'600', letterSpacing:'0.1em'}}>EVENING PROTOCOL</p>
            </div>
            {eveningHabits.map(habit => (
              <label key={habit.id} style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px', cursor:'pointer'}}>
                <input type="checkbox" checked={habit.completed} onChange={()=>toggleHabit(habit.id,false)} style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#4169E1'}} />
                <span className="mono" style={{fontSize:'11px', color:habit.completed?'#999':'#1A1A1A', textDecoration:habit.completed?'line-through':'none'}}>{habit.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="fade-in" style={{marginBottom:'40px'}}>
        <p className="mono" style={{fontSize:'11px', color:'#4169E1', marginBottom:'12px', letterSpacing:'0.1em'}}>[ REFLECTION ]</p>
        <div style={{border:'2px dotted #4169E1', padding:'4px', background:'white'}}>
          <div style={{display:'flex', gap:'8px', alignItems:'center', padding:'4px'}}>
            <input type="text" className="mono" value={reflectionText} onChange={(e)=>setReflectionText(e.target.value)}
              onKeyDown={(e)=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleSaveReflection();} }}
              placeholder="Quick thought or reflection..."
              style={{flex:1, padding:'12px 16px', fontSize:'13px', background:'white', border:'none', outline:'none'}}
            />
            <button className="mono" onClick={handleSaveReflection} disabled={!reflectionText.trim()}
              style={{padding:'12px 24px', fontSize:'11px', fontWeight:'600', color:reflectionText.trim()?'#FFFFFF':'#999', background:reflectionText.trim()?'#4169E1':'#E5E7EB', border:'none', cursor:reflectionText.trim()?'pointer':'not-allowed', letterSpacing:'0.1em'}}>
              LOG
            </button>
          </div>
        </div>
        {showSavedFeedback && <p className="mono fade-in" style={{fontSize:'10px', color:'#4169E1', marginTop:'8px', letterSpacing:'0.1em'}}>✓ REFLECTION SAVED TO ARCHIVE</p>}
      </div>
    </div>
  );
};

// ==================== CHECK-IN MODAL ====================
const CheckInModal = ({ onComplete }) => {
  const moods = [
    { id:'calm', label:'CALM', icon:Heart },
    { id:'happy', label:'HAPPY', icon:Sun },
    { id:'sad', label:'SAD', icon:Cloud },
    { id:'anxious', label:'ANXIOUS', icon:AlertCircle },
    { id:'tired', label:'TIRED', icon:Moon },
    { id:'stressed', label:'STRESSED', icon:Zap },
  ];

  const handleMoodSelect = (moodId) => {
    onComplete({ mood:moodId, timestamp:new Date() });
  };

  return (
    <div style={{position:'fixed', inset:0, background:'rgba(245,243,240,0.98)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
      <div style={{maxWidth:'800px', width:'100%'}} className="fade-in">
        <div style={{textAlign:'center', marginBottom:'60px'}}>
          <h1 className="mono" style={{fontSize:'clamp(32px, 6vw, 56px)', fontWeight:'600', color:'#4169E1', letterSpacing:'0.05em', marginBottom:'16px'}}>CHECK-IN</h1>
          <p className="mono" style={{fontSize:'13px', color:'#666', letterSpacing:'0.1em'}}>[ HOW ARE YOU FEELING RIGHT NOW? ]</p>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:'16px', maxWidth:'600px', margin:'0 auto'}}>
          {moods.map(mood => {
            const Icon=mood.icon;
            return (
              <button key={mood.id} className="mono" onClick={()=>handleMoodSelect(mood.id)}
                style={{padding:'32px 20px', background:'white', border:'2px dotted #4169E1', cursor:'pointer', transition:'all 0.2s', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px'}}
                onMouseEnter={(e)=>{ e.currentTarget.style.background='#F0F4FF'; e.currentTarget.style.transform='translateY(-4px)'; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.background='white'; e.currentTarget.style.transform='translateY(0)'; }}>
                <Icon size={32} color="#4169E1" strokeWidth={1.5} />
                <span style={{fontSize:'12px', fontWeight:'600', letterSpacing:'0.1em'}}>{mood.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ==================== MEDITATION ====================
const MeditationSession = () => {
  const [isMeditating, setIsMeditating] = useState(false);
  const [meditationTime, setMeditationTime] = useState(0);
  const [voiceGuideEnabled, setVoiceGuideEnabled] = useState(false);
  const meditationInterval = useRef(null);
  const meditationTimeoutsRef = useRef([]);

  const formatTime = (seconds) => {
    const mins=Math.floor(seconds/60), secs=seconds%60;
    return `${mins}:${secs.toString().padStart(2,'0')}`;
  };

  const speakMeditationGuide = () => {
    if ('speechSynthesis' in window) {
      meditationTimeoutsRef.current = [];
      const stages = [
        { text:"Welcome... Find a comfortable position.", delay:0 },
        { text:"Breathe in... deeply... through your nose.", delay:10000 },
        { text:"Slowly... exhale... through your mouth.", delay:20000 },
        { text:"Feel your body... relaxing.", delay:30000 },
        { text:"Focus... on your natural... breathing rhythm.", delay:40000 },
        { text:"If your mind wanders... gently... return to your breath.", delay:55000 },
        { text:"You are safe... You are present... You are calm.", delay:75000 },
        { text:"Continue... at your own pace.", delay:95000 },
        { text:"When you're ready... slowly... open your eyes.", delay:115000 }
      ];
      const loadVoices = () => {
        const voices=window.speechSynthesis.getVoices();
        const preferredVoiceNames=['Google UK English Female','Google US English Female','Microsoft Zira','Samantha','Karen','Victoria','Fiona'];
        let selectedVoice=null;
        for (const name of preferredVoiceNames) { selectedVoice=voices.find(v=>v.name.includes(name)&&v.lang.startsWith('en')); if (selectedVoice) break; }
        if (!selectedVoice) selectedVoice=voices.find(v=>v.name.toLowerCase().includes('female')&&v.lang.startsWith('en'));
        if (!selectedVoice) selectedVoice=voices.find(v=>v.lang.startsWith('en'));
        stages.forEach(stage => {
          const timeoutId=setTimeout(()=>{
            if (!window.speechSynthesis) return;
            if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
            const utterance=new SpeechSynthesisUtterance(stage.text);
            utterance.rate=0.65; utterance.pitch=0.9; utterance.volume=0.8;
            if (selectedVoice) utterance.voice=selectedVoice;
            try { window.speechSynthesis.speak(utterance); } catch (error) { console.error('Speech error:', error); }
          }, stage.delay);
          meditationTimeoutsRef.current.push(timeoutId);
        });
      };
      if (window.speechSynthesis.getVoices().length>0) loadVoices();
      else window.speechSynthesis.onvoiceschanged=loadVoices;
    }
  };

  const startMeditation = () => {
    setIsMeditating(true); setMeditationTime(0);
    meditationInterval.current=setInterval(()=>setMeditationTime(prev=>prev+1),1000);
    if (voiceGuideEnabled) speakMeditationGuide();
  };

  const stopMeditation = () => {
    setIsMeditating(false);
    if (meditationInterval.current) clearInterval(meditationInterval.current);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    meditationTimeoutsRef.current.forEach(id=>clearTimeout(id));
    meditationTimeoutsRef.current=[];
  };

  useEffect(() => {
    if (!voiceGuideEnabled) {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      meditationTimeoutsRef.current.forEach(id=>clearTimeout(id));
      meditationTimeoutsRef.current=[];
    } else if (voiceGuideEnabled&&isMeditating) speakMeditationGuide();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceGuideEnabled]);

  useEffect(() => {
    return () => {
      if (meditationInterval.current) clearInterval(meditationInterval.current);
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      meditationTimeoutsRef.current.forEach(id=>clearTimeout(id));
    };
  }, []);

  return (
    <div className="fade-in" style={{maxWidth:'1000px', margin:'0 auto', textAlign:'center'}}>
      <div style={{marginBottom:'32px', border:'2px dotted #4169E1', padding:'16px 20px', background:'white'}}>
        <h2 className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#4169E1', marginBottom:'8px'}}>MEDITATION SESSION</h2>
        <p className="mono" style={{fontSize:'11px', color:'#666'}}>Guided breathing and mindfulness exercise.</p>
      </div>
      <div style={{marginBottom:'40px'}}>
        <button className="mono" onClick={()=>setVoiceGuideEnabled(!voiceGuideEnabled)} style={{padding:'12px 24px', background:voiceGuideEnabled?'#4169E1':'white', color:voiceGuideEnabled?'#FFFFFF':'#1A1A1A', border:'2px solid #4169E1', cursor:'pointer', fontSize:'11px', letterSpacing:'0.1em'}}>
          {voiceGuideEnabled?'[ VOICE: ENABLED ]':'[ VOICE: DISABLED ]'}
        </button>
      </div>
      <div style={{marginBottom:'48px', display:'flex', justifyContent:'center'}}>
        <div style={{position:'relative', width:'200px', height:'200px'}}>
          <div style={{position:'absolute', inset:0, border:'2px dotted #4169E1', borderRadius:'50%', opacity:0.3}} />
          <div style={{position:'absolute', inset:'20px', border:'2px dotted #4169E1', borderRadius:'50%', opacity:0.5}} />
          <div style={{position:'absolute', inset:'40px', borderRadius:'50%', background:'#4169E1', opacity:isMeditating?1:0.3, animation:isMeditating?'breathe 4s infinite ease-in-out':'none'}} />
        </div>
      </div>
      <div style={{border:'2px dotted #4169E1', padding:'32px', background:'white', marginBottom:'40px'}}>
        <p className="mono" style={{fontSize:'10px', color:'#4169E1', marginBottom:'12px'}}>[ SESSION TIMER ]</p>
        <div className="mono" style={{fontSize:'52px', fontWeight:'300', letterSpacing:'0.05em'}}>{formatTime(meditationTime)}</div>
      </div>
      <button className="mono" onClick={isMeditating?stopMeditation:startMeditation} style={{padding:'16px 48px', fontSize:'12px', color:isMeditating?'#1A1A1A':'#FFFFFF', background:isMeditating?'white':'#4169E1', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>
        {isMeditating?'[ TERMINATE ]':'→ INITIALIZE'}
      </button>
    </div>
  );
};

// ==================== MAIN APP ====================
const MentalHealthTracker = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [showCheckIn, setShowCheckIn] = useState(true);
  const [checkInData, setCheckInData] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedSound, setSelectedSound] = useState(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);

  useEffect(() => { return () => stopSound(); }, []);

  const playSound = (soundId) => {
    stopSound();
    const audioFiles = { 1:'/audio/nature.mp3', 2:'/audio/ocean.mp3', 3:'/audio/rain.mp3', 4:'/audio/zen.mp3' };
    try {
      const audio=new Audio(audioFiles[soundId]);
      audio.loop=true; audio.volume=0.5; audio.play();
      oscillatorRef.current=audio; setSelectedSound(soundId); setIsPlaying(true);
    } catch (error) { console.error('Audio error:', error); alert('Could not play audio.'); }
  };

  const stopSound = () => {
    if (oscillatorRef.current) {
      try { if (oscillatorRef.current.pause) { oscillatorRef.current.pause(); oscillatorRef.current.currentTime=0; } else oscillatorRef.current.stop(); } catch (e) {}
      oscillatorRef.current=null;
    }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} audioContextRef.current=null; }
    setIsPlaying(false); setSelectedSound(null);
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'home': return <HomePage currentMood={checkInData?.mood} />;
      case 'companion': return <AICompanion currentMood={checkInData?.mood} />;
      case 'calm':
        return (
          <div className="fade-in" style={{maxWidth:'1000px', margin:'0 auto'}}>
            <div style={{marginBottom:'32px', border:'2px dotted #4169E1', padding:'16px 20px', background:'white'}}>
              <h2 className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#4169E1', marginBottom:'8px'}}>ACOUSTIC ENVIRONMENT</h2>
              <p className="mono" style={{fontSize:'11px', color:'#666'}}>Select ambient soundscape for relaxation.</p>
            </div>
            <p className="mono" style={{fontSize:'11px', color:'#4169E1', marginBottom:'16px', letterSpacing:'0.1em'}}>[ AVAILABLE SOUNDSCAPES ]</p>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'16px', marginBottom:'40px'}}>
              {[
                { id:1, name:'NATURE SOUNDS', image:'/images/nature.jpg', fallbackColor:'#95E1D3' },
                { id:2, name:'OCEAN WAVES', image:'/images/ocean.jpg', fallbackColor:'#4A90E2' },
                { id:3, name:'RAIN SOUNDS', image:'/images/rain.jpg', fallbackColor:'#A8DADC' },
                { id:4, name:'ZEN SOUNDS', image:'/images/zen.jpg', fallbackColor:'#52B788' }
              ].map(sound => (
                <div key={sound.id} onClick={()=>playSound(sound.id)} style={{border:'2px solid #4169E1', overflow:'hidden', cursor:'pointer', transition:'all 0.2s'}}
                  onMouseEnter={(e)=>{ e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(65,105,225,0.2)'; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}>
                  <div style={{width:'100%', height:'180px', position:'relative', display:'flex', alignItems:'center', justifyContent:'center', background:`linear-gradient(135deg, ${sound.fallbackColor}, ${sound.fallbackColor}DD)`}}>
                    <img src={sound.image} alt={sound.name} style={{position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0}} onError={(e)=>{e.currentTarget.style.display='none';}} />
                    {selectedSound===sound.id&&isPlaying && (
                      <div style={{position:'absolute', inset:0, background:'rgba(65,105,225,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1}}>
                        <div style={{width:'40px', height:'40px', border:'3px solid white', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', animation:'breathe 2s infinite ease-in-out'}}>
                          <Volume2 size={20} color="white" strokeWidth={2} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{padding:'12px', background:selectedSound===sound.id&&isPlaying?'#4169E1':'white', borderTop:'2px solid #4169E1'}}>
                    <p className="mono" style={{fontSize:'10px', fontWeight:'600', color:selectedSound===sound.id&&isPlaying?'#FFFFFF':'#1A1A1A', letterSpacing:'0.1em', textAlign:'center'}}>{sound.name}</p>
                  </div>
                </div>
              ))}
            </div>
            {isPlaying && (
              <div className="fade-in" style={{textAlign:'center'}}>
                <button className="mono" onClick={stopSound} style={{padding:'12px 24px', fontSize:'11px', color:'#FFFFFF', background:'#FF6B6B', border:'2px solid #FF6B6B', cursor:'pointer', letterSpacing:'0.1em'}}>■ STOP AUDIO</button>
              </div>
            )}
          </div>
        );
      case 'meditate': return <MeditationSession />;
      case 'games':
        return (
          <div className="fade-in" style={{maxWidth:'1000px', margin:'0 auto'}}>
            <div style={{marginBottom:'32px', border:'2px dotted #4169E1', padding:'16px 20px', background:'white'}}>
              <h2 className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#4169E1', marginBottom:'8px'}}>INTERACTIVE EXERCISES</h2>
              <p className="mono" style={{fontSize:'11px', color:'#666'}}>Stress-relief activities.</p>
            </div>
            <GamesMenu />
          </div>
        );
      case 'vent':
        return (
          <div className="fade-in" style={{maxWidth:'1000px', margin:'0 auto'}}>
            <div style={{marginBottom:'32px', border:'2px dotted #4169E1', padding:'16px 20px', background:'white'}}>
              <h2 className="mono" style={{fontSize:'16px', fontWeight:'600', color:'#4169E1', marginBottom:'8px'}}>PRIVATE VENTING ROOM</h2>
              <p className="mono" style={{fontSize:'11px', color:'#666'}}>Express yourself freely.</p>
            </div>
            <VentingRoom />
          </div>
        );
      case 'history': return <Archive />;
      default: return null;
    }
  };

  const pageLabels = {
    home:'HOME', companion:'COMPANION', calm:'SOUNDS', meditate:'MEDITATE', games:'GAMES', vent:'VENT ROOM', history:'ARCHIVE'
  };

  const navPages = ['home', 'companion', 'calm', 'meditate', 'games', 'vent', 'history'];

  return (
    <div style={{minHeight:'100vh', background:'#F5F3F0'}}>
      <GlobalStyles />

      {showCheckIn && (
        <CheckInModal onComplete={(data)=>{ setCheckInData(data); setShowCheckIn(false); }} />
      )}

      <header style={{position:'fixed', top:0, left:0, right:0, background:'white', borderBottom:'2px solid #4169E1', zIndex:1000}}>
        <div style={{padding:'20px 40px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px dotted #DDD', position:'relative'}}>
          <h2 className="mono" style={{fontSize:'20px', fontWeight:'700', color:'#4169E1', letterSpacing:'0.1em'}}>MINDFUL</h2>
          <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
            <button className="mono" onClick={()=>setShowCheckIn(true)}
              style={{padding:'8px 16px', fontSize:'11px', fontWeight:'600', color:'white', background:'#4169E1', border:'2px solid #4169E1', cursor:'pointer', letterSpacing:'0.1em'}}>
              + CHECK-IN
            </button>
            <button onClick={()=>setShowDropdown(!showDropdown)} style={{background:'none', border:'2px solid #4169E1', cursor:'pointer', padding:'8px 12px', fontSize:'16px', fontWeight:'700', color:'#4169E1'}}>⋯</button>
          </div>

          {showDropdown && (
            <div style={{position:'absolute', top:'100%', right:'40px', background:'white', border:'2px solid #4169E1', zIndex:1001, minWidth:'200px', marginTop:'8px'}}>
              {navPages.map(pageId => (
                <button key={pageId} className="mono" onClick={()=>{ setCurrentPage(pageId); setShowDropdown(false); }}
                  style={{width:'100%', padding:'12px 16px', fontSize:'11px', fontWeight:'500', textAlign:'left', color:currentPage===pageId?'#4169E1':'#1A1A1A', background:currentPage===pageId?'#F0F4FF':'white', border:'none', borderBottom:'1px dotted #DDD', cursor:'pointer', letterSpacing:'0.1em'}}>
                  {pageLabels[pageId]}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav style={{padding:'0 40px', display:'flex', gap:'0', overflowX:'auto'}}>
          {navPages.map(pageId => (
            <button key={pageId} className="mono" onClick={()=>setCurrentPage(pageId)}
              style={{padding:'16px 24px', fontSize:'11px', color:currentPage===pageId?'#4169E1':'#666', background:'transparent', border:'none', borderBottom:currentPage===pageId?'2px solid #4169E1':'2px solid transparent', cursor:'pointer', letterSpacing:'0.1em', whiteSpace:'nowrap'}}>
              {pageLabels[pageId]}
            </button>
          ))}
        </nav>
      </header>

      <main style={{padding:'140px 40px 80px', minHeight:'100vh', overflowY:'auto'}}>
        {renderContent()}
      </main>

      <footer style={{background:'white', borderTop:'2px dotted #4169E1'}}>
        <div style={{padding:'24px 40px', background:'#FFF5F5'}}>
          <div style={{maxWidth:'1000px', margin:'0 auto'}}>
            <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px'}}>
              <AlertCircle size={16} color="#FF6B6B" strokeWidth={2} />
              <p className="mono" style={{fontSize:'10px', color:'#FF6B6B', letterSpacing:'0.1em', fontWeight:'600'}}>[ EMERGENCY PROTOCOLS ]</p>
            </div>
            <p className="mono" style={{fontSize:'11px', color:'#333'}}>EMERGENCY → 112 | KIRAN (24/7) → 1800-599-0019 | PREVENTION → 096111 94949</p>
          </div>
        </div>
        <div style={{padding:'24px 40px'}}>
          <p className="mono" style={{fontSize:'10px', color:'#999', textAlign:'center', letterSpacing:'0.1em'}}>[ {pageLabels[currentPage]} ] • MINDFUL</p>
        </div>
      </footer>
    </div>
  );
};

const App = () => (
  <EntriesProvider>
    <MentalHealthTracker />
  </EntriesProvider>
);

export default App;
